import React, { useEffect, useState } from 'react';
import GameBoard from './GameBoard';
import GuessInput from './GuessInput';
import WordInsight from './WordInsight';
import GameHistory from './GameHistory';
import GameReview from './GameReview';
import TopBar from './TopBar';
import { createGame, submitGuess, getWordInfo, listGames, getGame, ApiError } from '../repository/gameRepository';
import { Game, GameSummary, DictionaryInfo } from '../types';
import { setupAutoRefresh } from '../utils/tokenRefresh';

const MainGame: React.FC = () => {
  const [difficulty, setDifficulty] = useState<'basic' | 'hard'>('basic');
  const [currentGame, setCurrentGame] = useState<Game | null>(null);
  const [games, setGames] = useState<GameSummary[]>([]);
  const [reviewGame, setReviewGame] = useState<Game | null>(null);
  const [lastInsight, setLastInsight] = useState<DictionaryInfo | null>(null);
  const [insightLoading, setInsightLoading] = useState(false);
  const [lastGuessedWord, setLastGuessedWord] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    window.location.href = '/login';
  };

  const refreshHistory = async () => {
    try {
      const history = await listGames(30);
      setGames(history);

      const active = history.find((game) => game.status === 'active');
      if (active) {
        const loaded = await getGame(active.uuid);
        setCurrentGame(loaded);
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        handleLogout();
      }
    }
  };

  useEffect(() => {
    setupAutoRefresh();
    refreshHistory();
  }, []);

  const handleNewGame = async () => {
    setLoading(true);
    try {
      const game = await createGame(difficulty);
      setCurrentGame(game);
      setLastInsight(null);
      setLastGuessedWord(null);
      await refreshHistory();
    } finally {
      setLoading(false);
    }
  };

  const handleGuessSubmit = async (guess: string) => {
    if (!currentGame) {
      throw new Error('Start a new game first.');
    }
    const result = await submitGuess(currentGame.uuid, guess);
    setCurrentGame(result.game);
    setLastGuessedWord(guess);
    refreshHistory();

    // Fetch word info in the background
    setInsightLoading(true);
    setLastInsight(null);
    getWordInfo(guess).then((info) => {
      setLastInsight(info);
    }).finally(() => setInsightLoading(false));
  };

  const handleSelectHistory = async (gameId: string) => {
    const game = await getGame(gameId);
    setReviewGame(game);
  };

  const gameInactive = !currentGame || currentGame.status !== 'active';

  return (
    <div className="app-shell">
      <TopBar
        difficulty={difficulty}
        onDifficultyChange={setDifficulty}
        onNewGame={handleNewGame}
        onLogout={handleLogout}
      />
      <div className="main-grid">
        <div>
          <div className="panel">
            {currentGame && <div className="badge">{currentGame.status}</div>}
            <h2 style={{ marginTop: 12 }}>Guess the word</h2>
            <GameBoard guesses={currentGame?.guesses || []} maxGuesses={6} />
            {currentGame?.status === 'lost' && currentGame.target_word && (
              <div className="reveal-word">
                The word was <strong>{currentGame.target_word}</strong>
              </div>
            )}
            {gameInactive ? (
              <div className="new-game-prompt">
                <button className="new-game-button" onClick={handleNewGame} disabled={loading}>
                  {loading ? 'Starting...' : 'New Game'}
                </button>
              </div>
            ) : (
              <GuessInput
                disabled={loading}
                onSubmit={handleGuessSubmit}
              />
            )}
          </div>
        </div>
        <div>
          <WordInsight info={lastInsight} loading={insightLoading} lastGuessedWord={lastGuessedWord} />
          <GameHistory games={games} onSelect={handleSelectHistory} />
          <GameReview game={reviewGame} />
        </div>
      </div>
    </div>
  );
};

export default MainGame;
